// useAutoFit.js
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * @param {Object} opts
 * @param {number} opts.maxHeight           - px (ör: 120)
 * @param {number} [opts.minScale=0.7]      - en küçük font ölçeği
 * @param {number} [opts.minGap=0.6]        - boşluk için en küçük çarpan (1 = normal, 0.6 = %40 kıs)
 * @param {number} [opts.precision=0.005]   - ikili arama hassasiyeti
 * @param {boolean} [opts.observeResize=true]
 */
export function useAutoFit({
  maxHeight,
  minScale = 0.7,
  minGap = 0.6,
  precision = 0.005,
  observeResize = true,
}) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  const [gap, setGap] = useState(1);

  const fitsAt = (el, s, g) => {
    el.style.setProperty("--fit-scale", String(s));
    el.style.setProperty("--fit-gap", String(g));
    return el.scrollHeight <= maxHeight;
  };

  const binSearch = (lo, hi, test) => {
    while (hi - lo > precision) {
      const mid = (lo + hi) / 2;
      if (test(mid)) {
        lo = mid; // sığıyor: daha büyüğünü dene
      } else {
        hi = mid; // sığmıyor: küçült
      }
    }
    return Number(lo.toFixed(3));
  };

  const measureAndFit = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // reset
    el.style.setProperty("--fit-scale", "1");
    el.style.setProperty("--fit-gap", "1");

    // 1) zaten sığıyor mu?
    if (fitsAt(el, 1, 1)) {
      setScale(1);
      setGap(1);
      return;
    }

    // 2) Önce boşlukları kısmayı dene (gap: 1 → minGap)
    if (fitsAt(el, 1, minGap)) {
      // gap aralığında ikili arama (öncelik boşluk)
      const bestGap = binSearch(minGap, 1, (g) => fitsAt(el, 1, g));
      setGap(bestGap);
      setScale(1);
      return;
    }

    // 3) En küçük gapa kadar kısınca bile sığmadıysa gap = minGap'e sabitle
    el.style.setProperty("--fit-gap", String(minGap));
    setGap(minGap);

    // 4) Şimdi fontu küçült (scale: 1 → minScale)
    if (!fitsAt(el, minScale, minGap)) {
      // minScale’de bile sığmıyorsa minScale’e fixle (daha küçültme yok)
      setScale(minScale);
      el.style.setProperty("--fit-scale", String(minScale));
      return;
    }

    const bestScale = binSearch(minScale, 1, (s) => fitsAt(el, s, minGap));
    setScale(bestScale);
    el.style.setProperty("--fit-scale", String(bestScale));
  }, [maxHeight, minGap, minScale, precision]);

  useLayoutEffect(() => {
    measureAndFit();
  }, [measureAndFit]);

  useEffect(() => {
    if (!observeResize || !ref.current) return;
    
    let timeoutId;
    const ro = new ResizeObserver(() => {
      // Debounce ile ResizeObserver'ı sınırla
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        measureAndFit();
      }, 16); // ~60fps
    });
    
    ro.observe(ref.current);
    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
    };
  }, [observeResize, measureAndFit]);

  return { ref, scale, gap, recompute: measureAndFit };
}
